import {
  Box,
  Divider,
  Group,
  Table as MantineTable,
  NumberInput,
  ScrollArea,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm, UseFormReturnType } from '@mantine/form';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { AgentSource } from '../../../api/events-client/events-client.type';
import { useBulkUpsertAgentSpending } from '../../../api/financials-client/financials-client.hooks';
import type {
  AgentSpending,
  AgentSpendingBulkUpsertRequest,
} from '../../../api/financials-client/financials-client.type';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import { getCurrency } from '../../../helpers/currency-helpers';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { baseWhite, inkLight, secondaryBase, skyDark, skyLightest } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Icon } from '../../../ui-library/icon/icon';
import { Tooltip } from '../../../ui-library/tooltip/tooltip';
import { toast } from '../../notifications';
import { AGENT_SOURCES_ICONS, AGENT_SOURCES_LABELS } from '../agentic-process.type';

dayjs.extend(localeData);

type AgentSpendingForm = AgentSpending & { key: string };

type Props = {
  agentSpending: AgentSpending[];
  selectedYear: number;
};

export const AgenticFinancialsSpendingTable = ({ agentSpending, selectedYear }: Props) => {
  const organizationId = useGlobalStore((s) => s.organization?.id)!;
  const organization = useGlobalStore((s) => s.organization);
  const { symbol: currencySymbol } = getCurrency(organization);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedAgentSource, setSelectedAgentSource] = useState<string | null>(null);

  const { bulkUpsertAgentSpending, isUpserting } = useBulkUpsertAgentSpending();

  const monthLabels = dayjs().localeData().monthsShort();

  const form = useForm<{ expenditures: AgentSpendingForm[] }>({
    initialValues: {
      expenditures:
        agentSpending && agentSpending.length > 0
          ? agentSpending.map((e) => ({
              key: `${e.agent_source}-${e.year}-${e.month}`,
              ...e,
            }))
          : [],
    },
  });

  // Reinitialize form when data changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: adding form methods causes infinite loop
  useEffect(() => {
    if (agentSpending && agentSpending.length > 0) {
      form.resetDirty();
      form.initialize({
        expenditures: (agentSpending ?? []).map((e) => ({
          key: `${e.agent_source}-${e.year}-${e.month}`,
          ...e,
        })),
      });
    }
  }, [agentSpending]);

  // Get unique agent sources from the data and enum
  const agentSources = useMemo(() => {
    const allSources = Object.values(AgentSource);
    const filteredSources = allSources.filter((source) => source.toLowerCase().includes(searchTerm.toLowerCase()));
    return filteredSources;
  }, [searchTerm]);

  // Get agent sources that have spending data (including form data)
  const agentSourcesWithData = useMemo(() => {
    const formAgentSources = new Set(form.values.expenditures.map((e) => e.agent_source));
    const dataAgentSources = new Set(agentSpending.map((spending) => spending.agent_source));
    const allWithData = new Set([...formAgentSources, ...dataAgentSources]);

    return agentSources.filter((source) => allWithData.has(source));
  }, [agentSources, agentSpending, form.values.expenditures]);

  // Get agent sources that don't have spending data (for adding new rows)
  const agentSourcesWithoutData = useMemo(() => {
    const formAgentSources = new Set(form.values.expenditures.map((e) => e.agent_source));
    const dataAgentSources = new Set(agentSpending.map((spending) => spending.agent_source));
    const allWithData = new Set([...formAgentSources, ...dataAgentSources]);

    return agentSources.filter((source) => !allWithData.has(source));
  }, [agentSources, agentSpending, form.values.expenditures]);

  const buildAgentSpendingPayload = (
    expenditures: AgentSpendingForm[],
    selectedYear: number,
  ): AgentSpendingBulkUpsertRequest => {
    // Group expenditures by agent source
    const agentSourcesData: Partial<Record<AgentSource, { month: number; budget: number | null }[]>> = {};

    expenditures.forEach((expenditure) => {
      const { agent_source, month, budget } = expenditure;

      if (!agentSourcesData[agent_source]) {
        agentSourcesData[agent_source] = [];
      }

      agentSourcesData[agent_source]!.push({
        month,
        budget,
      });
    });

    return {
      organization_id: organizationId,
      year: selectedYear,
      agent_sources: agentSourcesData,
    };
  };

  const handleSaveChanges = async () => {
    try {
      const expenditures = form.getValues().expenditures;
      const payload = buildAgentSpendingPayload(expenditures, selectedYear);

      await bulkUpsertAgentSpending(payload);
      form.resetDirty();
      toast.success('Your data has been saved successfully!');
    } catch (error) {
      console.error('Error saving agent spending data:', error);
      toast.error('There was an error saving your data. Please try again.');
    }
  };

  return (
    <Fragment>
      <BrandedLoadingOverlay visible={isUpserting} variant="colored" />
      <Stack>
        <Group justify="space-between">
          <Group>
            <Text fw={700} size="lg">
              Agent Sources
            </Text>
            <Tooltip
              position="bottom"
              label="Enter your monthly subscription cost per agent source. When a budget is set, costs are calculated using a cost-per-token rate (budget / total tokens) instead of API-based billing. If no budget is entered, costs default to API pay-per-use pricing."
              variant="black"
              offset={8}
              multiline
              w={320}
            >
              <Icon name="info" size={18} color={secondaryBase} style={{ cursor: 'pointer' }} />
            </Tooltip>
          </Group>
          <Group>
            {form.isDirty() && (
              <Group>
                <Button
                  variant="primary"
                  onClick={handleSaveChanges}
                  radius="xl"
                  size="xs"
                  leftSection={<Icon name="check_circle" color={baseWhite} size={16} />}
                >
                  Save Changes
                </Button>
                <Button
                  variant="outline"
                  onClick={() => form.reset()}
                  radius="xl"
                  size="xs"
                  leftSection={<Icon name="cancel" color={inkLight} size={16} />}
                >
                  Cancel
                </Button>
              </Group>
            )}
            <TextInput
              placeholder="Search for Agent Source"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              leftSection={<Icon name="search" />}
              radius="xl"
              style={{ width: 400 }}
            />
          </Group>
        </Group>
        <Divider />
        <MantineTable verticalSpacing="5" withRowBorders={false} style={{ tableLayout: 'fixed', minWidth: 800 }}>
          <colgroup>
            <col style={{ width: '100%' }} />
            {monthLabels.map((label, index) => (
              <col
                key={label}
                style={{
                  backgroundColor:
                    selectedYear === dayjs().year() && index + 1 > dayjs().month() + 1 ? skyLightest : baseWhite,
                }}
              />
            ))}
          </colgroup>
          <ScrollArea w="100%" h="100%" offsetScrollbars type="always" scrollbarSize={10} scrollbars="x">
            <Box w="100%">
              <MantineTable.Thead>
                <MantineTable.Tr py={24}>
                  <MantineTable.Th style={{ minWidth: 200 }}>
                    <Text fw={700}>Agent Source</Text>
                  </MantineTable.Th>
                  {monthLabels.map((m) => (
                    <MantineTable.Th
                      key={m}
                      style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      <Text c={skyDark}>{m}</Text>
                    </MantineTable.Th>
                  ))}
                </MantineTable.Tr>
              </MantineTable.Thead>
              <MantineTable.Tbody>
                {agentSourcesWithData.map((agentSource) => (
                  <AgentSourceRow
                    key={agentSource}
                    organizationId={organizationId}
                    agentSource={agentSource}
                    selectedYear={selectedYear}
                    form={form}
                    currencySymbol={currencySymbol}
                  />
                ))}
                {agentSourcesWithoutData.length > 0 && (
                  <MantineTable.Tr>
                    <MantineTable.Td colSpan={13}>
                      <Group style={{ marginTop: 16 }}>
                        <Icon name="add_circle" size={26} />
                        <Select
                          placeholder="Select agent source to add"
                          value={selectedAgentSource}
                          data={agentSourcesWithoutData.map((agentSource) => ({
                            value: agentSource,
                            label: AGENT_SOURCES_LABELS[agentSource],
                          }))}
                          onChange={(value) => {
                            if (value) {
                              const agentSource = value as AgentSource;
                              // Add a new row for this agent source
                              const firstMonthKey = `${agentSource}-${selectedYear}-1`;
                              form.insertListItem('expenditures', {
                                key: firstMonthKey,
                                organization_id: organizationId,
                                agent_source: agentSource,
                                year: selectedYear,
                                month: 1,
                                budget: null,
                              });
                              // Clear the selection after adding
                              setSelectedAgentSource(null);
                            }
                          }}
                          clearable
                          searchable
                          style={{ width: 250 }}
                          radius="xl"
                          renderOption={({ option }) => (
                            <Group gap="xs">
                              <img src={AGENT_SOURCES_ICONS[option.value as AgentSource]} height={16} />
                              <Text size="sm">{option.label}</Text>
                            </Group>
                          )}
                        />
                      </Group>
                    </MantineTable.Td>
                  </MantineTable.Tr>
                )}
              </MantineTable.Tbody>
            </Box>
          </ScrollArea>
        </MantineTable>
      </Stack>
    </Fragment>
  );
};

function AgentSourceRow({
  organizationId,
  agentSource,
  selectedYear,
  form,
  currencySymbol,
}: {
  organizationId: string;
  agentSource: AgentSource;
  selectedYear: number;
  form: UseFormReturnType<{ expenditures: AgentSpendingForm[] }>;
  currencySymbol: string;
}) {
  const expenditures = form.values.expenditures;

  return (
    <MantineTable.Tr py={24}>
      <MantineTable.Td>
        <Group gap="xs">
          <img src={AGENT_SOURCES_ICONS[agentSource]} height={12} />
          <Text size="small">{AGENT_SOURCES_LABELS[agentSource]}</Text>
        </Group>
      </MantineTable.Td>
      {dayjs()
        .localeData()
        .months()
        .map((_, idx) => {
          const year = selectedYear;
          const month = idx + 1;
          const key = `${agentSource}-${year}-${month}`;
          const i = expenditures.findIndex((r) => r.key === key);

          // existing row → controlled input
          if (i >= 0) {
            return (
              <MantineTable.Td key={month}>
                <NumberInput
                  hideControls
                  style={{ width: 120 }}
                  value={form.values.expenditures[i]?.budget ?? undefined}
                  onChange={(value) => {
                    const newBudget = value === '' || value === undefined || value === null ? null : Number(value);
                    if (newBudget === null) {
                      // Remove the entire expenditure entry when budget is cleared
                      form.removeListItem('expenditures', i);
                    } else {
                      // Update the budget value
                      form.setFieldValue(`expenditures.${i}.budget`, newBudget);
                    }
                  }}
                  key={key}
                  radius="md"
                  prefix={currencySymbol}
                  thousandSeparator=","
                  decimalScale={0}
                />
              </MantineTable.Td>
            );
          }

          // new row → blank input that inserts on first change
          return (
            <MantineTable.Td key={month}>
              <NumberInput
                disabled={selectedYear === dayjs().year() && month > dayjs().month() + 1}
                hideControls
                style={{ width: 120 }}
                value={undefined}
                onChange={(value) => {
                  const newBudget = value === '' || value === undefined || value === null ? null : Number(value);
                  form.insertListItem('expenditures', {
                    key,
                    organization_id: organizationId,
                    agent_source: agentSource,
                    year,
                    month,
                    budget: newBudget,
                  });
                }}
                key={key}
                radius="md"
                prefix={currencySymbol}
                thousandSeparator=","
                decimalScale={0}
              />
            </MantineTable.Td>
          );
        })}
    </MantineTable.Tr>
  );
}
